// components/form/Form.js
import { useState, createContext, useContext } from 'react';
import { useUnsavedChangesWarning } from 'hooks/useUnsavedChangesWarning';

// Create context with undefined so misuse is detectable
const FormContext = createContext(undefined);

export function useForm() {
	const ctx = useContext(FormContext);
	// Return null instead of throwing to support standalone field usage
	return ctx;
}

// Lightweight deep strip of __typename (no lodash/omit-deep)
function stripTypename(input) {
	if (input instanceof Date) return input;
	if (Array.isArray(input)) {
		return input.map(stripTypename);
	}
	if (input && typeof input === 'object') {
		const out = {};
		for (const [k, v] of Object.entries(input)) {
			if (k === '__typename') continue;
			out[k] = stripTypename(v);
		}
		return out;
	}
	return input;
}

// Safer "is empty" for required fields
function isEmptyValue(v) {
	return (
		v === undefined ||
		v === null ||
		(typeof v === 'string' && v.trim() === '') ||
		(Array.isArray(v) && v.length === 0)
	);
}

export default function Form(props) {
	const [fields, setFields] = useState({});
	const [values, setValues] = useState(props.values || {});
	const [errors, setErrors] = useState({});
	const [preSubmitHooks, setPreSubmitHooks] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isInitialized, setIsInitialized] = useState(false);

	const [hasUnsavedChanges, setHasUnsavedChanges, WarningDialog] =
		useUnsavedChangesWarning(false, {
			interceptLinks: !!props.warnUnsavedChanges,
			message:
				props.unsavedChangesMessage ||
				'Du har ulagrede endringer. Er du sikker på at du vil forlate siden?',
		});

	function registerField(field) {
		if (!field?.name) {
			throw new Error('Field must have a name');
		}

		setFields(prev => {
			if (field.disabled) return prev;
			if (prev[field.name]) return prev;
			return { ...prev, [field.name]: field };
		});

		// Seed value only if it's required and not already set in form `values`
		const provided = field.value ?? field.defaultValue;
		if (field.required && provided !== undefined) {
			setValues(prev =>
				Object.prototype.hasOwnProperty.call(prev, field.name)
					? prev
					: { ...prev, [field.name]: provided }
			);
		}
	}

	function updateField(field) {
		if (!field || !field.name) return;

		setValues(prev => ({
			...prev,
			[field.name]: field.value,
		}));

		// Mark dirty on user edits (not during first-time seeding)
		if (props.warnUnsavedChanges && !hasUnsavedChanges && isInitialized) {
			setHasUnsavedChanges(true);
		}

		if (!isInitialized) {
			setIsInitialized(true);
		}

		if (field.error) {
			setErrors(prev => ({ ...prev, [field.name]: field.error }));
		} else {
			setErrors(prev => {
				const next = { ...prev };
				delete next[field.name];
				return next;
			});
		}
	}

	function validateField(field, value) {
		if (field?.required && isEmptyValue(value)) {
			return false;
		}

		if (field?.pattern) {
			if (!field.required && isEmptyValue(value)) return true;
			const re = new RegExp(field.pattern);
			return re.test(String(value));
		}

		if (typeof field?.validate === 'function') {
			return field.validate(value);
		}

		return true;
	}

	function isFieldsValid(currentFields, currentValues) {
		const names = Object.keys(currentFields);
		if (names.length === 0) return false;
		for (const name of names) {
			if (!validateField(currentFields[name], currentValues[name])) {
				return false;
			}
		}
		return true;
	}

	function validateFields() {
		const names = Object.keys(fields);
		const nextErrors = {};
		let ok = true;

		for (const name of names) {
			const fld = fields[name];
			const valid = validateField(fld, values[name]);
			if (!valid) {
				ok = false;
				nextErrors[name] = fld;
			}
		}

		setErrors(nextErrors);
		return ok;
	}

	async function runPreSubmitHooks() {
		let next = values;
		for (const key in preSubmitHooks) {
			// hooks can return partials
			const patch = await preSubmitHooks[key](next);
			if (patch) next = { ...next, ...patch };
		}
		return next;
	}

	async function onSubmit(e) {
		try {
			e.preventDefault();
			e.stopPropagation();

			if (props.isDisabled) return;
			if (typeof e.target?.reportValidity === 'function') {
				if (!e.target.reportValidity()) return;
			}

			setIsSubmitting(true);

			// Run hooks and cleanup, then validate
			const afterHooks = await runPreSubmitHooks();
			const cleaned = stripTypename(afterHooks);
			setValues(cleaned); // keep UI in sync

			const submitValues = cleaned;
			const ok = validateFields();

			if (ok) {
				let success = true;
				try {
					await props.onSubmit(submitValues);
					if (props.warnUnsavedChanges) setHasUnsavedChanges(false);
				} catch (err) {
					console.error(err);
					success = false;
				}

				if (success && typeof props.onSuccess === 'function') {
					props.onSuccess(submitValues);
				}
			}
		} catch (err) {
			console.error(err);
		} finally {
			setIsSubmitting(false);
		}
	}

	const {
		errorMessage,
		children,
		isDisabled,
		isLoading,
		renderHiddenSubmit = true,
		style,
	} = props;

	return (
		<FormContext.Provider
			value={{
				fields,
				values,
				errors,
				isLoading: isLoading || isSubmitting,
				isDisabled: isDisabled,
				updateField: field => updateField(field),
				registerField: field => registerField(field),
				canSubmit: isFieldsValid,
				errorMessage: errorMessage || 'Dette feltet er påkrevd',
				registerPreSubmitHook: (name, callback) => {
					setPreSubmitHooks(prev => ({ ...prev, [name]: callback }));
				},
				removePreSubmitHook: name => {
					setPreSubmitHooks(prev => {
						const next = { ...prev };
						delete next[name];
						return next;
					});
				},
			}}
		>
			<form onSubmit={onSubmit} style={style}>
				{renderHiddenSubmit && (
					<input
						type="submit"
						style={{
							position: 'absolute',
							left: '-9999px',
							width: '1px',
							height: '1px',
							opacity: '0',
						}}
						aria-hidden="true"
						tabIndex={-1}
					/>
				)}

				{children}
			</form>

			{props.warnUnsavedChanges && <WarningDialog />}
		</FormContext.Provider>
	);
}
